/**
 * ConfirmDialog — window.confirm 대체 (사용자 페이지 화이트톤 2버튼 모달)
 * 어드민 ConfirmDialog와 동일 패턴, 화이트톤
 *
 * 사용:
 *   const { confirm, dialog } = useConfirm();
 *   const ok = await confirm(t("common.unsaved_changes"));
 *   if (!ok) return;
 *   ...
 *   return (<>...{dialog}</>);
 */
import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePopupTopOffset } from "../../hooks/usePopupTopOffset";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface PendingConfirm {
  message: string;
  resolve: (ok: boolean) => void;
}

export function useConfirm() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const popupTop = usePopupTopOffset();
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef, pending !== null);

  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => setPending({ message, resolve }));
  }, []);

  const handle = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialog = pending ? (
    <div ref={containerRef} className="fixed left-0 right-0 bottom-0 z-[100] flex items-center justify-center bg-black/40" style={{ top: popupTop }} onClick={(e) => { e.stopPropagation(); handle(false); }}>
      <div
        className="w-full max-w-sm rounded-2xl border border-slate-300 bg-white p-6 shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-sm text-slate-900 text-center mb-6 whitespace-pre-wrap">{pending.message}</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => handle(false)}
            className="rounded-lg border border-slate-300 px-5 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors min-w-[80px]"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={() => handle(true)}
            className="rounded-lg bg-amber-500 px-5 py-1.5 text-sm font-bold text-white hover:bg-amber-400 transition-colors min-w-[80px]"
          >
            {t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}
